import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Shield, FileText, Users, Settings, Bell, Database, AlertCircle, Plus, Award, ScrollText } from 'lucide-react';

export default function Miscellaneous() {
  const [tab, setTab] = useState('vessel');
  const tabs = [
    { key: 'vessel', label: 'Vessel Profile', icon: Ship },
    { key: 'certificates', label: 'Certificates', icon: Award },
    { key: 'notices', label: 'Notice Board', icon: Bell },
    { key: 'oil-analysis', label: 'Oil Analysis', icon: ScrollText },
    { key: 'users', label: 'User Management', icon: Users },
    { key: 'settings', label: 'System Settings', icon: Settings },
    { key: 'backup', label: 'Data Backup', icon: Database },
  ];

  return (
    <div>
      <div className="tabs">
        {tabs.map(t => {
          const Icon = t.icon;
          return <div key={t.key} className={`tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}><Icon size={14} />{t.label}</div>;
        })}
      </div>
      {tab === 'vessel' && <VesselProfileTab />}
      {tab === 'certificates' && <CertificatesTab />}
      {tab === 'notices' && <NoticesTab />}
      {tab === 'oil-analysis' && <OilAnalysisTab />}
      {tab === 'users' && <UsersTab />}
      {tab === 'settings' && <SettingsTab />}
      {tab === 'backup' && <BackupTab />}
    </div>
  );
}

// Vessel Profile
function VesselProfileTab() {
  const [profile, setProfile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/reports/vessel-profile').then(r => { setProfile(r.data); setForm(r.data); }).catch(console.error); }, []);

  const handleSave = async () => {
    setSaving(true);
    try { await api.put('/reports/vessel-profile', form); setEditMode(false); api.get('/reports/vessel-profile').then(r => setProfile(r.data)); }
    catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  if (!profile) return <div className="loading-screen"><div className="spinner"></div></div>;

  const fields = [
    { label: 'Vessel Name', key: 'name' },
    { label: 'IMO Number', key: 'imo_number' },
    { label: 'Flag State', key: 'flag_state' },
    { label: 'Port of Registry', key: 'port_of_registry' },
    { label: 'Vessel Type', key: 'vessel_type' },
    { label: 'Gross Tonnage', key: 'gross_tonnage' },
    { label: 'DWT', key: 'dwt' },
    { label: 'Year Built', key: 'year_built' },
    { label: 'Classification Society', key: 'classification_society' },
    { label: 'Class Number', key: 'class_number' },
    { label: 'Engine Maker', key: 'engine_maker' },
    { label: 'Engine Type', key: 'engine_type' },
    { label: 'Engine Output (kW)', key: 'engine_output_kw' },
    { label: 'Call Sign', key: 'call_sign' },
    { label: 'MMSI Number', key: 'mmsi_number' },
    { label: 'Company', key: 'company_name' },
    { label: 'Currency Default', key: 'currency_default' },
  ];

  return (
    <div>
      <div className="flex-between mb-16">
        <h3>{profile.name}</h3>
        <button className="btn btn-primary" onClick={() => setEditMode(!editMode)}>{editMode ? 'Cancel' : 'Edit Profile'}</button>
      </div>
      <div className="card">
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {fields.map(f => (
              <div key={f.key}>
                <label className="form-label">{f.label}</label>
                {editMode ? (
                  <input className="form-input" value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
                ) : (
                  <p style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', margin: 0 }}>{profile[f.key] || '—'}</p>
                )}
              </div>
            ))}
          </div>
          {editMode && (
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => { setEditMode(false); setForm(profile); }}>Cancel</button>
              <button className="btn btn-accent" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
            </div>
          )}
        </div>
      </div>
      <div className="stat-cards mt-16">
        <div className="stat-card"><div className="stat-content"><div className="stat-value">{profile.totalComponents}</div><div className="stat-label">Equipment Items</div></div></div>
        <div className="stat-card"><div className="stat-content"><div className="stat-value">{profile.totalWorkOrders}</div><div className="stat-label">Work Orders</div></div></div>
        <div className="stat-card"><div className="stat-content"><div className="stat-value text-success">{profile.closedWOs}</div><div className="stat-label">Closed WOs</div></div></div>
      </div>
    </div>
  );
}

// Certificates
function CertificatesTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { api.get('/misc/certificates').then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false)); }, []);

  return (
    <div>
      <div className="flex-between mb-16">
        <p className="text-secondary text-sm">Track vessel certificates with renewal reminders.</p>
        <button className="btn btn-accent btn-sm" onClick={() => setShowCreate(true)}><Plus size={14} /> Add Certificate</button>
      </div>
      <div className="card">
        {loading ? <div className="card-body">{[...Array(3)].map((_, i) => <div key={i} className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div>)}</div> :
         <div className="table-container">
           <table>
             <thead><tr><th>Certificate</th><th>Number</th><th>Issuer</th><th>Issue Date</th><th>Expiry Date</th><th>Status</th></tr></thead>
             <tbody>
               {data.map(c => (
                 <tr key={c.id}>
                   <td><strong>{c.certificate_name}</strong></td>
                   <td>{c.certificate_number || '—'}</td>
                   <td>{c.issuing_authority}</td>
                   <td>{c.issue_date}</td>
                   <td>{c.expiry_date}</td>
                   <td><span className={`badge badge-${c.status.toLowerCase().replace(' ', '')}`}>{c.status}</span></td>
                 </tr>
               ))}
             </tbody>
           </table>
         </div>
        }
      </div>
      {showCreate && <CreateCertModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); api.get('/misc/certificates').then(r => setData(r.data)); }} />}
    </div>
  );
}

function CreateCertModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ certificate_name: '', certificate_number: '', issuing_authority: '', issue_date: '', expiry_date: '', renewal_reminder_days: 90 });
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await api.post('/misc/certificates', form); onCreated(); }
    catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>Add Certificate</h2><button className="btn btn-outline btn-sm" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group"><label className="form-label">Certificate Name *</label><input className="form-input" value={form.certificate_name} onChange={e => setForm({ ...form, certificate_name: e.target.value })} required /></div>
            <div className="form-group"><label className="form-label">Certificate Number</label><input className="form-input" value={form.certificate_number} onChange={e => setForm({ ...form, certificate_number: e.target.value })} /></div>
            <div className="form-group"><label className="form-label">Issuing Authority *</label><input className="form-input" value={form.issuing_authority} onChange={e => setForm({ ...form, issuing_authority: e.target.value })} required /></div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Issue Date *</label><input className="form-input" type="date" value={form.issue_date} onChange={e => setForm({ ...form, issue_date: e.target.value })} required /></div>
              <div className="form-group"><label className="form-label">Expiry Date *</label><input className="form-input" type="date" value={form.expiry_date} onChange={e => setForm({ ...form, expiry_date: e.target.value })} required /></div>
            </div>
            <div className="form-group"><label className="form-label">Renewal Reminder (days before expiry)</label><input className="form-input" type="number" value={form.renewal_reminder_days} onChange={e => setForm({ ...form, renewal_reminder_days: parseInt(e.target.value) })} /></div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>Add Certificate</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Notices
function NoticesTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { api.get('/misc/notices').then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false)); }, []);

  const acknowledge = async (id) => {
    try { await api.post(`/misc/notices/${id}/acknowledge`); api.get('/misc/notices').then(r => setData(r.data)); }
    catch (e) { console.error(e); }
  };

  return (
    <div>
      <div className="flex-between mb-16">
        <p className="text-secondary text-sm">Notices and bulletins from shore office.</p>
        <button className="btn btn-accent btn-sm" onClick={() => setShowCreate(true)}><Plus size={14} /> Post Notice</button>
      </div>
      <div style={{ display: 'grid', gap: 12 }}>
        {loading ? <div className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div> :
         data.length === 0 ? <div className="empty-state"><Bell /><h4>No notices</h4></div> :
         data.map(n => (
           <div key={n.id} className="card">
             <div className="card-body">
               <div className="flex-between mb-8">
                 <h4 style={{ fontSize: 14 }}>{n.title}</h4>
                 <span className="text-secondary text-sm">{n.posted_date}</span>
               </div>
               <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>{n.content}</p>
               <div className="flex-between">
                 <span className="text-secondary text-sm">Posted by: {n.posted_by_name}</span>
                 <button className="btn btn-outline btn-sm" onClick={() => acknowledge(n.id)}>Acknowledge</button>
               </div>
             </div>
           </div>
         ))
        }
      </div>
      {showCreate && <CreateNoticeModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); api.get('/misc/notices').then(r => setData(r.data)); }} />}
    </div>
  );
}

function CreateNoticeModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: '', content: '', target_all: true });
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await api.post('/misc/notices', form); onCreated(); }
    catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>Post Notice</h2><button className="btn btn-outline btn-sm" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group"><label className="form-label">Title *</label><input className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required /></div>
            <div className="form-group"><label className="form-label">Content</label><textarea className="form-textarea" value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} /></div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}><input type="checkbox" checked={form.target_all} onChange={e => setForm({ ...form, target_all: e.target.checked })} /> Post to all vessels</label>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>Post Notice</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Oil Analysis
function OilAnalysisTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { api.get('/misc/oil-analysis').then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false)); }, []);

  return (
    <div>
      <div className="flex-between mb-16">
        <p className="text-secondary text-sm">Track oil analysis lab results. AI monitors trends for predictive maintenance.</p>
        <button className="btn btn-accent btn-sm" onClick={() => setShowCreate(true)}><Plus size={14} /> Add Record</button>
      </div>
      <div className="card">
        {loading ? <div className="card-body">{[...Array(3)].map((_, i) => <div key={i} className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div>)}</div> :
         data.length === 0 ? <div className="empty-state"><ScrollText /><h4>No oil analysis records</h4></div> :
         <div className="table-container">
           <table>
             <thead><tr><th>Equipment</th><th>Sample Date</th><th>Running Hours</th><th>Viscosity</th><th>TBN</th><th>Iron (ppm)</th><th>Lab Rec.</th><th>AI Trend</th></tr></thead>
             <tbody>
               {data.map(r => (
                 <tr key={r.id}>
                   <td><strong>{r.component_name}</strong></td>
                   <td>{r.sample_date}</td>
                   <td>{r.running_hours?.toLocaleString()}</td>
                   <td>{r.viscosity_cst || '—'}</td>
                   <td>{r.tbn || '—'}</td>
                   <td style={{ color: r.iron_ppm > 50 ? 'var(--warning)' : 'var(--text)' }}>{r.iron_ppm || '—'}</td>
                   <td><span className={`badge ${r.lab_recommendation === 'OK' ? 'badge-healthy' : r.lab_recommendation === 'Change Oil' ? 'badge-warning' : 'badge-watch'}`}>{r.lab_recommendation}</span></td>
                   <td style={{ fontSize: 11, color: '#7c3aed' }}>{r.ai_trend_analysis || '—'}</td>
                 </tr>
               ))}
             </tbody>
           </table>
         </div>
        }
      </div>
      {showCreate && <CreateOilModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); api.get('/misc/oil-analysis').then(r => setData(r.data)); }} />}
    </div>
  );
}

function CreateOilModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ sample_date: '', running_hours: '', viscosity_cst: '', tbn: '', iron_ppm: '', lab_recommendation: 'OK' });
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await api.post('/misc/oil-analysis', form); onCreated(); }
    catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>Add Oil Analysis Record</h2><button className="btn btn-outline btn-sm" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group"><label className="form-label">Sample Date *</label><input className="form-input" type="date" value={form.sample_date} onChange={e => setForm({ ...form, sample_date: e.target.value })} required /></div>
              <div className="form-group"><label className="form-label">Running Hours</label><input className="form-input" type="number" value={form.running_hours} onChange={e => setForm({ ...form, running_hours: parseFloat(e.target.value) })} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Viscosity (cSt)</label><input className="form-input" type="number" value={form.viscosity_cst} onChange={e => setForm({ ...form, viscosity_cst: parseFloat(e.target.value) })} /></div>
              <div className="form-group"><label className="form-label">TBN</label><input className="form-input" type="number" value={form.tbn} onChange={e => setForm({ ...form, tbn: parseFloat(e.target.value) })} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Iron (ppm)</label><input className="form-input" type="number" value={form.iron_ppm} onChange={e => setForm({ ...form, iron_ppm: parseFloat(e.target.value) })} /></div>
              <div className="form-group"><label className="form-label">Lab Recommendation</label>
                <select className="form-select" value={form.lab_recommendation} onChange={e => setForm({ ...form, lab_recommendation: e.target.value })}>
                  {['OK','Monitor','Change Oil','Investigate'].map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>Add Record</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Users
function UsersTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { api.get('/misc/users').then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false)); }, []);

  const toggleActive = async (user) => {
    try { await api.put(`/misc/users/${user.id}`, { is_active: !user.is_active }); api.get('/misc/users').then(r => setData(r.data)); }
    catch (e) { console.error(e); }
  };

  return (
    <div>
      <div className="flex-between mb-16">
        <p className="text-secondary text-sm">Manage user accounts and role permissions.</p>
        <button className="btn btn-accent btn-sm" onClick={() => setShowCreate(true)}><Plus size={14} /> Add User</button>
      </div>
      <div className="card">
        {loading ? <div className="card-body">{[...Array(3)].map((_, i) => <div key={i} className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div>)}</div> :
         <div className="table-container">
           <table>
             <thead><tr><th>Name</th><th>Username</th><th>Email</th><th>Role</th><th>Vessel</th><th>Status</th><th>Last Login</th><th>Actions</th></tr></thead>
             <tbody>
               {data.map(u => (
                 <tr key={u.id}>
                   <td><strong>{u.full_name}</strong></td>
                   <td>{u.username}</td>
                   <td>{u.email}</td>
                   <td><span className="badge badge-info">{u.role?.replace(/_/g, ' ')}</span></td>
                   <td>{u.vessel_name || 'Shore'}</td>
                   <td><span className={`badge ${u.is_active ? 'badge-healthy' : 'badge-rejected'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                   <td>{u.last_login ? u.last_login.split('T')[0] : 'Never'}</td>
                   <td><button className="btn btn-outline btn-sm" onClick={() => toggleActive(u)}>{u.is_active ? 'Deactivate' : 'Activate'}</button></td>
                 </tr>
               ))}
             </tbody>
           </table>
         </div>
        }
      </div>
      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); api.get('/misc/users').then(r => setData(r.data)); }} />}
    </div>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ username: '', email: '', password: '', full_name: '', role: 'second_engineer', vessel_id: '' });
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await api.post('/auth/register', form); onCreated(); }
    catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>Add User</h2><button className="btn btn-outline btn-sm" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group"><label className="form-label">Full Name</label><input className="form-input" value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} required /></div>
              <div className="form-group"><label className="form-label">Username</label><input className="form-input" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} required /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required /></div>
              <div className="form-group"><label className="form-label">Password</label><input className="form-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Role</label>
                <select className="form-select" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                  {['chief_engineer','second_engineer','third_engineer','electrical_officer','bosun','fleet_superintendent','technical_manager','procurement_officer','shore_admin'].map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>Create User</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// System Settings
function SettingsTab() {
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/misc/settings').then(r => setSettings(r.data)).catch(console.error); }, []);

  const handleSave = async (key, value) => {
    setSaving(true);
    try { await api.put('/misc/settings', { [key]: value }); }
    catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const settingsList = [
    { key: 'sync_frequency_hours', label: 'Sync Frequency (hours)', type: 'number' },
    { key: 'currency_default', label: 'Default Currency', type: 'select', options: ['USD', 'EUR', 'SGD', 'GBP'] },
    { key: 'running_hours_unit', label: 'Running Hours Unit', type: 'select', options: ['hours', 'starts', 'cycles'] },
    { key: 'po_approval_threshold', label: 'PO Approval Threshold (USD)', type: 'number' },
    { key: 'alert_email', label: 'Alert Email', type: 'text' },
  ];

  return (
    <div className="card">
      <div className="card-header"><h3>System Settings</h3><span className="text-secondary text-sm">Only Shore Admin can modify these settings.</span></div>
      <div className="card-body">
        {settingsList.map(s => (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
            <div>
              <div style={{ fontWeight: 500, fontSize: 13 }}>{s.label}</div>
            </div>
            <div>
              {s.type === 'select' ? (
                <select className="form-select" style={{ width: 160 }} defaultValue={settings[s.key]} onChange={e => handleSave(s.key, e.target.value)}>
                  {s.options.map(o => <option key={o}>{o}</option>)}
                </select>
              ) : (
                <input className="form-input" type={s.type} style={{ width: 160 }} defaultValue={settings[s.key]} onBlur={e => handleSave(s.key, e.target.value)} />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Backup
function BackupTab() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => { api.get('/misc/backups').then(r => setBackups(r.data)).catch(console.error).finally(() => setLoading(false)); }, []);

  const createBackup = async () => {
    setCreating(true);
    try { await api.post('/misc/backups'); api.get('/misc/backups').then(r => setBackups(r.data)); }
    catch (e) { console.error(e); }
    finally { setCreating(false); }
  };

  return (
    <div>
      <div className="flex-between mb-16">
        <p className="text-secondary text-sm">Automatic daily backup runs at midnight vessel time. Shore backup created on each sync.</p>
        <button className="btn btn-accent btn-sm" onClick={createBackup} disabled={creating}>{creating ? 'Creating...' : 'Create Manual Backup'}</button>
      </div>
      <div className="card">
        {loading ? <div className="card-body">{[...Array(3)].map((_, i) => <div key={i} className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div>)}</div> :
         backups.length === 0 ? <div className="empty-state"><Database /><h4>No backups yet</h4></div> :
         <div className="table-container">
           <table><thead><tr><th>Date</th><th>Type</th><th>Size</th><th>Status</th><th>Actions</th></tr></thead>
             <tbody>{backups.map(b => <tr key={b.id}><td>{b.created_at}</td><td><span className="badge badge-draft">{b.backup_type}</span></td><td>{b.backup_size_mb ? `${b.backup_size_mb} MB` : '—'}</td><td><span className={`badge ${b.status === 'Success' ? 'badge-healthy' : 'badge-warning'}`}>{b.status}</span></td><td><button className="btn btn-outline btn-sm">Restore</button></td></tr>)}</tbody>
           </table>
         </div>
        }
      </div>
    </div>
  );
}

// Fix: Ship icon
function Ship(props) {
  return <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 .6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76"/><path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6"/><path d="M12 10v4"/><path d="M12 2v3"/></svg>;
}
